/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import { ComponentController as BpController } from '@rta/rta-blueprint-component';
import { BlueprintInstanceType, Connection, CornerSize, HorizontalAlignment, Island, Obstruction, Panel, Size, useSubscription } from '@rta/rta-blueprint-library';
import { Box, FormLabel, Select, Stack } from '@chakra-ui/react';
import { useEffect, useState, type JSX } from 'react';

import { ContextPanelContent } from './context-panel-content';
import { InlineNumberInput } from './controls';
import { lang } from '@lang';
import { useDesign } from '@hooks';

export type ConnectionPropertiesPanelProps = {
	id: string | null;
};

export const ConnectionPropertiesPanel = ({ id }: ConnectionPropertiesPanelProps): JSX.Element => {
	const [controller] = useDesign<BpController>();
	const [sourceIsland, setSourceIsland] = useState<Island | Obstruction | null>(null);
	const [standardSize, setStandardSize] = useState<Size>();
	const [cornerSize, setCornerSize] = useState<CornerSize>();
	const readonly = controller?.readonly ?? true;

	useEffect(() => {
		if (controller) {
			let refMap = controller.kitchen?.getReferenceMap(id ?? '');
			if (refMap.type === BlueprintInstanceType.Countertop) refMap = controller.kitchen?.getReferenceMap(refMap.countertop!.islandIds[0]);

			// Handle both islands and obstructions
			const source = refMap.island ?? refMap.obstruction;
			setSourceIsland(source);

			if (refMap.island?.isCornerIsland) {
				setCornerSize(refMap.island.size as CornerSize);
			} else if (source) {
				setStandardSize(source.size as Size);
			}
		}
	}, [controller, id]);

	if (!controller || !sourceIsland || !(standardSize || cornerSize)) return <></>;

	const panelId = sourceIsland.instanceOf === BlueprintInstanceType.Island
		? id
		: (sourceIsland as Obstruction).panels.getFrontPanel()?.id;
	const [selectedConnection] = sourceIsland.connections.getConnectionsBySourcePanelId(panelId ?? '');

	if (!selectedConnection) return <></>;

	let sourceConnection: Connection | null = null;
	let targetConnection: Connection | null = null;

	if (selectedConnection.isSource) {
		sourceConnection = selectedConnection as Connection;
		({ targetConnection } = selectedConnection);
	} else {
		sourceConnection = selectedConnection.targetConnection;
		targetConnection = selectedConnection as Connection;
	}

	const { sourcePanel } = sourceConnection as Connection;
	const { sourcePanel: targetPanel } = targetConnection as Connection;

	let sourceLeftLabel = 'Right';
	let sourceRightLabel = 'Left';
	if (sourcePanel?.isFrontPanel) {
		sourceLeftLabel = 'Left';
		sourceRightLabel = 'Right';
	} else if (sourcePanel?.isLeftPanel) {
		sourceLeftLabel = 'Back';
		sourceRightLabel = 'Front';
	} else if (sourcePanel?.isRightPanel) {
		sourceLeftLabel = 'Front';
		sourceRightLabel = 'Back';
	}

	let targetLeftLabel = 'Right';
	let targetRightLabel = 'Left';
	if (targetPanel?.isFrontPanel) {
		targetLeftLabel = 'Left';
		targetRightLabel = 'Right';
	} else if (targetPanel?.isLeftPanel) {
		targetLeftLabel = 'Back';
		targetRightLabel = 'Front';
	} else if (targetPanel?.isRightPanel) {
		targetLeftLabel = 'Front';
		targetRightLabel = 'Back';
	}


	return <ContextPanelContent title={lang.contextPanels.title.connectionProperties}>
		<Box w="100%">
			<Stack spacing="12px" marginTop="20px">
				<InlineNumberInput
					label="Padding" min={-400} max={400}
					value={ sourceConnection?.padding ?? 0 }
					enabled={!readonly}
					onChange={(valueString) => {
						if (sourceConnection) {
							sourceConnection.padding = parseFloat(valueString);
						}
					}}
				/>

				<InlineNumberInput
					label="Offset" min={-400} max={400}
					value={ sourceConnection?.offset ?? 0 }
					enabled={!readonly}
					onChange={(valueString) => {
						if (sourceConnection) {
							sourceConnection.offset = parseFloat(valueString);
						}
					}}
				/>

				<Box>
					<FormLabel htmlFor="dimension-font-factor" fontSize="small">Source Horizontal Align:</FormLabel>
					<Select id="dimension-font-factor" fontSize="small"
						value={sourceConnection?.horizontalAlign}
						disabled={readonly}
						onChange={(event) => {
							if (sourceConnection) {
								sourceConnection.horizontalAlign = event.target.value as HorizontalAlignment;
							}
						}}>
						<option value={HorizontalAlignment.left}>{sourceLeftLabel} </option>
						<option value={HorizontalAlignment.right}>{sourceRightLabel}</option>
					</Select>
				</Box>

				<Box>
					<FormLabel htmlFor="dimension-font-factor" fontSize="small">Target Alignment:</FormLabel>
					<Select id="dimension-font-factor" fontSize="small"
						value={targetConnection?.horizontalAlign}
						disabled={readonly}
						onChange={(event) => {
							if (targetConnection) {
								targetConnection.horizontalAlign = event.target.value as HorizontalAlignment;
							}
						}}>
						<option value={HorizontalAlignment.left}>{targetLeftLabel}</option>
						<option value={HorizontalAlignment.right}>{targetRightLabel}</option>
					</Select>
				</Box>

			</Stack>
		</Box>
	</ContextPanelContent>;
};
